<template>
  <section class="mobile-pdf-viewer" aria-label="PDF">
    <div class="mobile-pdf-toolbar">
      <button type="button" :disabled="loading || pageNumber <= 1" @click="goToPage(pageNumber - 1)">
        ‹
      </button>
      <span v-if="pageCount">{{ pageNumber }} / {{ pageCount }}</span>
      <span v-else>{{ $t("filePreview.pdfLoading") }}</span>
      <button type="button" :disabled="loading || pageNumber >= pageCount" @click="goToPage(pageNumber + 1)">
        ›
      </button>
      <button type="button" :disabled="loading" :aria-label="$t('filePreview.zoomOutPdf')" @click="zoom(-0.2)">−</button>
      <button type="button" :disabled="loading" :aria-label="$t('filePreview.zoomInPdf')" @click="zoom(0.2)">＋</button>
    </div>
    <div class="mobile-pdf-page-wrap" @touchstart.passive="handleTouchStart" @touchend="handleTouchEnd">
      <div v-if="loading" class="mobile-pdf-state">
        <van-loading size="28" color="#4286f5" />
        <span>{{ $t("filePreview.pdfLoading") }}</span>
      </div>
      <div v-else-if="error" class="mobile-pdf-state">
        <van-icon name="warning-o" size="28" />
        <span>{{ $t("filePreview.pdfLoadFailed") }}</span>
        <button type="button" @click="loadDocument">{{ $t("filePreview.retry") }}</button>
        <button type="button" @click="$emit('download')">{{ $t("filePreview.downloadFile") }}</button>
        <button type="button" @click="openInBrowser">{{ $t("filePreview.openNewWindow") }}</button>
      </div>
      <canvas v-show="!loading && !error" ref="canvas" class="mobile-pdf-canvas"></canvas>
    </div>
  </section>
</template>

<script>
import { loadPdfLibrary } from "@/utils/documentCover";

export default {
  name: "MobilePdfViewer",
  props: {
    url: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      error: false,
      pageNumber: 1,
      pageCount: 0,
      scale: 1,
      pdf: null,
      renderTask: null,
      renderId: 0,
      requestId: 0,
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  watch: {
    url: {
      immediate: true,
      handler() { this.loadDocument(); },
    },
  },
  beforeDestroy() {
    this.requestId++;
    this.destroyDocument();
  },
  methods: {
    async loadDocument() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = false;
      this.pageNumber = 1;
      this.pageCount = 0;
      this.scale = 1;
      this.destroyDocument();
      try {
        const pdfjs = await loadPdfLibrary();
        if (requestId !== this.requestId) return;
        const base = `${process.env.BASE_URL || "/"}pdf-cover-assets/`;
        const task = pdfjs.getDocument({
          url: this.url,
          cMapUrl: `${base}cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `${base}standard_fonts/`,
          wasmUrl: `${base}wasm/`,
          isEvalSupported: false,
        });
        this.pdf = await task.promise;
        if (requestId !== this.requestId) {
          await this.destroyDocument();
          return;
        }
        this.pageCount = this.pdf.numPages;
        await this.renderPage(requestId);
      } catch (error) {
        if (requestId === this.requestId) this.error = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async renderPage(requestId = this.requestId) {
      if (!this.pdf || !this.$refs.canvas || requestId !== this.requestId) return;
      const renderId = ++this.renderId;
      if (this.renderTask) {
        this.renderTask.cancel();
        this.renderTask = null;
      }
      this.loading = true;
      try {
        const page = await this.pdf.getPage(this.pageNumber);
        if (requestId !== this.requestId) return;
        const availableWidth = Math.max(240, this.$el.clientWidth - 24);
        const baseViewport = page.getViewport({ scale: 1 });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: (availableWidth / baseViewport.width) * this.scale });
        const canvas = this.$refs.canvas;
        const context = canvas.getContext("2d", { alpha: false });
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        this.renderTask = page.render({ canvasContext: context, viewport, transform: dpr === 1 ? null : [dpr, 0, 0, dpr, 0, 0] });
        await this.renderTask.promise;
      } catch (error) {
        if (error?.name !== "RenderingCancelledException" && requestId === this.requestId && renderId === this.renderId) this.error = true;
      } finally {
        if (requestId === this.requestId && renderId === this.renderId) {
          this.renderTask = null;
          this.loading = false;
        }
      }
    },
    async goToPage(page) {
      if (!this.pdf || page < 1 || page > this.pageCount || page === this.pageNumber) return;
      this.pageNumber = page;
      await this.renderPage();
    },
    async zoom(amount) {
      this.scale = Math.max(0.6, Math.min(2.4, this.scale + amount));
      await this.renderPage();
    },
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
      this.touchStartY = event.changedTouches?.[0]?.clientY || 0;
    },
    handleTouchEnd(event) {
      const endX = event.changedTouches?.[0]?.clientX || 0;
      const endY = event.changedTouches?.[0]?.clientY || 0;
      const delta = endX - this.touchStartX;
      if (Math.abs(delta) > 70 && Math.abs(delta) > Math.abs(endY - this.touchStartY) * 1.25) {
        this.goToPage(this.pageNumber + (delta < 0 ? 1 : -1));
      }
      this.touchStartX = 0;
      this.touchStartY = 0;
    },
    openInBrowser() { window.open(this.url, "_blank", "noopener"); },
    async destroyDocument() {
      this.renderId++;
      if (this.renderTask) {
        this.renderTask.cancel();
        this.renderTask = null;
      }
      if (this.pdf) {
        const pdf = this.pdf;
        this.pdf = null;
        await pdf.destroy().catch(() => {});
      }
    },
  },
};
</script>

<style scoped lang="less">
.mobile-pdf-viewer {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  background: #f5f6f8;
}
.mobile-pdf-toolbar {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-bottom: 1px solid #e8ebef;
  background: #fff;
  color: #303133;
  button {
    min-width: 38px;
    height: 34px;
    border: 1px solid #d8dee8;
    border-radius: 6px;
    color: #4286f5;
    background: #fff;
    font-size: 22px;
    &:disabled { color: #b9c2ce; }
  }
  span { min-width: 54px; text-align: center; font-size: 14px; }
}
.mobile-pdf-page-wrap {
  display: flex;
  min-height: 0;
  flex: 1;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  padding: 12px;
  touch-action: pan-y;
}
.mobile-pdf-canvas { max-width: none; background: #fff; box-shadow: 0 1px 6px rgba(0, 0, 0, .16); }
.mobile-pdf-state {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #909399;
  button { padding: 8px 16px; border: 1px solid #d8dee8; border-radius: 6px; color: #4286f5; background: #fff; }
}
</style>
